export interface NimbusSelectedOption {
  id: string
  text: string
  order: number
}

export interface NimbusQuestionBuildInput {
  question_id: string
  text: string
  group: string
  order: number
  question_type: 'text' | 'multi_select' | 'number'
  required?: boolean
  rawValue: unknown
  optionTextById?: Record<string, string>
  selectedOptionPairs?: Array<{ id: string; text: string }>
  treatEmptyAsAnswered?: boolean
}

export interface NimbusQuestionPayload {
  text: string
  group: string
  order: number
  options: NimbusSelectedOption[]
  required: boolean
  response: {
    answered: boolean
    metadata: {
      skipped: boolean
      time_spent_seconds: number
    }
    answered_at: string
    response_text: string | null
    response_number: number | null
    selected_options: NimbusSelectedOption[]
  }
  question_id: string
  question_type: 'text' | 'multi_select' | 'number'
}

function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  return false
}

function getOptionText(value: string, optionTextById?: Record<string, string>): string {
  return optionTextById?.[value] || value
}

function toSelectedOptions(
  input: NimbusQuestionBuildInput
): NimbusSelectedOption[] {
  if (input.selectedOptionPairs?.length) {
    return input.selectedOptionPairs.map((item, index) => ({
      id: item.id,
      text: item.text,
      order: index + 1,
    }))
  }

  const value = input.rawValue
  if (isEmptyValue(value)) return []

  if (Array.isArray(value)) {
    return value.map((item, index) => {
      const id = String(item)
      return {
        id,
        text: getOptionText(id, input.optionTextById),
        order: index + 1,
      }
    })
  }

  if (typeof value === 'boolean') {
    return [
      {
        id: value ? 'yes' : 'no',
        text: value ? 'Yes' : 'No',
        order: 1,
      },
    ]
  }

  if (typeof value === 'number') {
    return [{ id: String(value), text: String(value), order: 1 }]
  }

  const id = String(value)
  return [
    {
      id,
      text: getOptionText(id, input.optionTextById),
      order: 1,
    },
  ]
}

export function buildNimbusQuestion(
  input: NimbusQuestionBuildInput,
  answeredAt: string
): NimbusQuestionPayload {
  const selectedOptions = toSelectedOptions(input)
  const answered = input.treatEmptyAsAnswered ? true : selectedOptions.length > 0

  return {
    text: input.text,
    group: input.group,
    order: input.order,
    options: selectedOptions,
    required: input.required ?? true,
    response: {
      answered,
      metadata: {
        skipped: false,
        time_spent_seconds: 0,
      },
      answered_at: answeredAt,
      response_text: null,
      response_number: null,
      selected_options: selectedOptions,
    },
    question_id: input.question_id,
    question_type: input.question_type,
  }
}
